import BaseApi from "api/base";
import { BASE_AUTH } from "api/config";
import { CreateChannelRequest } from "api/models/Hive/createChannel";
import {
  HiveActivitiesModel,
  OnlineMembersModel,
} from "api/models/Hive/hiveActivities";
import { HiveChannelsModel } from "api/models/Hive/hiveChannels";
import { HiveComponents } from "api/models/Hive/hiveComponents";
import {
  ContactInfoModel,
  HiveDetails,
  HiveDetailsModel,
  HiveGuidelinesModel,
  PublicHivesModel,
  SocialLinksModel,
} from "api/models/Hive/hiveDetails";
import { ContextRequest, EditHiveRequest } from "api/models/Hive/hiveEdit";
import { MemberDetailItem, MembersModel } from "api/models/Hive/hiveMembers";
import {
  PresignedData,
  ResourcePreSignedModel,
} from "api/models/Hive/introVideo";
import { AxiosInstance, AxiosPromise } from "axios";
import { store } from "store";

import { BaseResponse } from "../../models/base";
import {
  ChatBotChatroomsReq,
  ChatRoomsByChannelModel,
  ChatBotChatroomMessagesReq,
  ChatBotGoodResponseReq,
} from "api/models/Chat/chat";

export class HivePageApi extends BaseApi {
  getHiveType(): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/Profile/Get`,
      method: "POST",
    });
  }
  getHiveComponents(data: any): AxiosPromise<BaseResponse<HiveComponents>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "component" : "noAuth"
      }/main/list`,
      method: "POST",
      data,
    });
  }

  getChildComponents(data: any): AxiosPromise<BaseResponse<HiveComponents>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "component" : "noAuth"
      }/child/list`,
      method: "POST",
      data,
    });
  }

  getHiveChannels(data: {
    hiveId: number;
    isMemberView: boolean;
  }): AxiosPromise<BaseResponse<HiveChannelsModel>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "" : "noAuth/"
      }channel/list`,
      method: "POST",
      data,
    });
  }

  getHiveDetails(data: any): AxiosPromise<BaseResponse<HiveDetails>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "hive/" : "noAuth/"
      }details`,
      method: "POST",
      data,
    });
  }

  getHiveGuidelines(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<HiveDetails>> {
    return this.request({
      url: `${this.url}${
        localStorage.getItem("isLoggedIn") === "yes" ? "" : "/noAuth"
      }/guidelines/organization/list`,
      method: "POST",
      data,
    });
  }

  editHiveGuidelines(data: {
    guidelinesList: HiveGuidelinesModel[];
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/guidelines/edit`,
      method: "POST",
      data,
    });
  }

  addHiveGuidelines(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/guidelines/create`,
      method: "POST",
      data,
    });
  }

  deleteHiveGuidelines(data: {
    guidelineId: number;
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/guidelines/delete`,
      method: "POST",
      data,
    });
  }

  getHiveActivitiesUser(data: {
    profileId: string;
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<MemberDetailItem>> {
    return this.request({
      url: `${this.url}/activities/details/user`,
      method: "POST",
      data,
    });
  }

  getHiveListUser(data: {
    profileId: string;
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<MemberDetailItem>> {
    return this.request({
      url: `${this.url}/member/details/user`,
      method: "POST",
      data,
    });
  }

  getHiveActivities(data: {
    communityId: number;
    pageNo: number;
    contentLimit: number;
    channelUuid?: string;
  }): AxiosPromise<BaseResponse<HiveActivitiesModel>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "" : "noAuth/"
      }activities/get/all/hive`,
      method: "POST",
      data,
    });
  }

  getOnlineMembers(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<OnlineMembersModel[]>> {
    return this.request({
      url: `spring/analytics/cta/${
        localStorage.getItem("isLoggedIn") === "yes" ? "dashboard" : "unAuth"
      }/active/sessions`,

      method: "POST",
      data,
    });
  }

  getSocialLinks(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<SocialLinksModel>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "" : "noAuth/"
      }hive/social/handles`,
      method: "POST",
      data,
    });
  }

  getContactInfo(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<ContactInfoModel>> {
    return this.request({
      url: `${this.url}/hive/contact/details`,
      method: "POST",
      data,
    });
  }

  saveSocialLinks(data: {
    organizationUuid: string;
    componentCode: string;
    handleUrl: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/hive/save/social/handles`,
      method: "POST",
      data,
    });
  }

  saveContactInfo(data: {
    organizationUuid: string;
    email: string;
    address: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/hive/save/contact/details`,
      method: "POST",
      data,
    });
  }

  getPublicHives(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<PublicHivesModel>> {
    return this.request({
      url: `${this.url}/${
        localStorage.getItem("isLoggedIn") === "yes" ? "" : "noAuth/"
      }hive/suggested/hives`,
      method: "POST",
      data,
    });
  }

  getHiveContext(data: {
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<any>> {
    return this.request({
      url: `chat/bot/org/details/get`,
      method: "POST",
      data,
    });
  }

  editHiveContext(data: ContextRequest): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `chat/bot/org/details/update`,
      method: "POST",
      data,
    });
  }

  createChannel(
    data: CreateChannelRequest
  ): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/channel/create/channel`,
      method: "POST",
      data,
    });
  }

  editHiveDetails(data: EditHiveRequest): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/hive/edit`,
      method: "POST",
      data,
    });
  }

  getIntroPresignedUrl(data: {
    type: string;
    communityId: number;
  }): AxiosPromise<BaseResponse<ResourcePreSignedModel>> {
    return this.request({
      url: `/content/admin/resources/pre/signed`,
      method: "POST",
      data,
    });
  }

  getPresignedUrlWithType(data: {
    type: string;
    communityId: number;
    setUploadProgress: any;
    fileType: string;
  }): AxiosPromise<BaseResponse<ResourcePreSignedModel>> {
    return this.request({
      url: `/content/admin/resources/pre/signed`,
      method: "POST",
      data,
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / 80) * 100);
        data.setUploadProgress(progress);
      },
    });
  }

  getMembersList(data: {
    hiveId: number;
    pageNo: number;
    contentLimit: number;
    userName?: string;
    channelUuid?: string;
  }): AxiosPromise<BaseResponse<MembersModel>> {
    return this.request({
      url: `${this.url}/member/hive/list`,
      method: "POST",
      data,
    });
  }

  removeHiveMember(data: {
    userId: number;
    hiveId: number;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/delete`,
      method: "POST",
      data,
    });
  }

  addSuperAdmin(data: {
    userId: number;
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/add/super/admin`,
      method: "POST",
      data,
    });
  }

  removeChannelMember(data: {
    userId: number;
    channelId: number;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/remove/channel/members`,
      method: "POST",
      data,
    });
  }

  deleteHiveProduct(data: {
    productId: string;
    organizationUuid: string;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `/chat/bot/org/details/delete/products`,
      method: "POST",
      data,
    });
  }

  addHiveProduct(data: {
    productName: string;
    organizationUuid: string;
    productLink: string | null;
    productDesc: string | null;
    productUrl: string | null;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `/chat/bot/org/details/add/products`,
      method: "POST",
      data,
    });
  }

  uploadHiveProduct(data: {
    productId: string;
    organizationUuid: string;
    contentType: string;
  }): AxiosPromise<BaseResponse<PresignedData>> {
    return this.request({
      url: `/chat/bot/org/details/upload/products`,
      method: "POST",
      data,
    });
  }

  editHiveProductName(data: {
    productId: string;
    productName: string;
    organizationUuid: string;
    productUrl: string;
    productLink: string | null;
    productDesc: string | null;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `/chat/bot/org/details/edit/products`,
      method: "POST",
      data,
    });
  }

  emailMemberInvite(data: {
    users: {
      email: string;
    }[];
    channels: string[];
    communityId: number;
    skipEmails: boolean;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/add/email`,
      method: "POST",
      data,
    });
  }
  emailAdminInvite(data: {
    users: {
      email: string;
    }[];
    channels: string[];
    communityId: number;
    skipEmails: boolean;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/add/email/admin`,
      method: "POST",
      data,
    });
  }

  phoneMemberInvite(data: {
    users: {
      mobileNo: string;
    }[];
    channels: string[];
    communityId: number;
    skipEmails: boolean;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/add/phone`,
      method: "POST",
      data,
    });
  }
  phoneAdminInvite(data: {
    users: {
      mobileNo: string;
    }[];
    channels: string[];
    communityId: number;
    skipEmails: boolean;
  }): AxiosPromise<BaseResponse<boolean>> {
    return this.request({
      url: `${this.url}/member/add/phone/admin`,
      method: "POST",
      data,
    });
  }

  getChatbotChatrooms(
    data: ChatBotChatroomsReq
  ): AxiosPromise<BaseResponse<ChatRoomsByChannelModel>> {
    return this.request({
      url: `/chat/bot/get/chat/rooms`,
      method: "POST",
      data,
    });
  }

  getChatbotChatroomMessages(
    data: ChatBotChatroomMessagesReq
  ): AxiosPromise<BaseResponse<ChatRoomsByChannelModel>> {
    return this.request({
      url: `/chat/bot/get/chat/messages`,
      method: "POST",
      data,
    });
  }

  markChatbotGoodResponse(
    data: ChatBotGoodResponseReq
  ): AxiosPromise<BaseResponse<ChatRoomsByChannelModel>> {
    return this.request({
      url: `/chat/bot/update/good/response`,
      method: "POST",
      data,
    });
  }
}

export default function hivePageApi(request: AxiosInstance) {
  return new HivePageApi({
    request,
    url: `/webApp`,
  });
}
